// Third party libraries
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CardNumberElement, CardExpiryElement, CardCvcElement, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { Form } from 'semantic-ui-react';
import { PaymentRequest, Stripe } from '@stripe/stripe-js';

// Styled components
import OButton from '../../styled/button';

// Models
import PaymentModel, { NewSubscriptionInterface } from '../../../models/payment';

// Redux
import { openLoader, closeLoader } from '../../../redux-store/loader';

// Locales
import I18n from '../../../i18n';

// Interfaces
interface PropsInterface {
    planId: string;
    offerId?: string;
    amount: number;
    currency: string;
    description: string;
    elements: any;
    onError: (error: any) => void;
    onSuccess: () => void;
    stripe: Stripe | null;
    type: string;
};

const StripeCheckoutForm = ({ planId, offerId, amount, currency, description, elements, onError, onSuccess, stripe, type }: PropsInterface) => {

    const dispatch = useDispatch();
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
    const [formProcessing, setFormProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (stripe && amount && currency) {
            try {
                const pr = stripe.paymentRequest({
                    country: 'US',
                    currency: currency.toLowerCase(),
                    total: {
                        label: description || 'Demo total',
                        amount: amount * 100,
                    },
                    requestPayerName: true,
                    requestPayerEmail: true,
                });

                pr.canMakePayment().then((result: any) => {
                    if (result) {
                        setPaymentRequest(pr);
                    }
                });

                pr.on('paymentmethod', async (event: any) => {
                    try {
                        // NOTA: Si usas Apple/Google Pay, aquí deberías implementar 
                        // la misma lógica de enviar el event.paymentMethod.id a tu backend
                        event.complete('success');
                    } catch (error) {
                        event.complete('fail');
                        onError(error);
                    }
                });

            } catch (error) {
                console.error(error);
            }
        }
    }, [amount, currency, description, onError, stripe]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!formProcessing && stripe !== null) {
            setFormProcessing(true);
            dispatch(openLoader());

            try {
                // 1. Crear el método de pago (PaymentMethod) con la tarjeta del formulario
                const { paymentMethod, error } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: elements.getElement('cardNumber'),
                });

                if (error) throw new Error(error.message);
                if (!paymentMethod || !paymentMethod.card) throw new Error("No card information available");

                // 2. Preparar los datos y enviarlos al backend para CREAR LA SUSCRIPCIÓN
                const subscriptionData: NewSubscriptionInterface = {
                    planId,
                    amount: amount * 100,
                    currency: currency,
                    paymentMethodId: paymentMethod.id,
                    platform: 'stripe',
                    type,
                    offerId: offerId || undefined
                };

                const subscription = await PaymentModel.createSubscription(subscriptionData);

                // 3. CONTROL CRÍTICO DE 3D SECURE (SCA)
                // Tu backend debe retornar si la suscripción requiere acción ('requires_action' o 'incomplete')
                // y adjuntar el clientSecret del PaymentIntent de la primera factura de Stripe.
                if (subscription.status === 'requires_action' || subscription.status === 'incomplete') {
                    if (subscription.clientSecret) {

                        // Lanzamos el modal nativo de Stripe para que el profesor valide el pago en su app bancaria/SMS
                        const { paymentIntent, error } = await stripe.confirmCardPayment(subscription.clientSecret);

                        if (error) {
                            // Manejar el error del modal (el usuario canceló o falló)
                            throw new Error(error.message);
                        } else if (paymentIntent.status === 'succeeded') {
                            // ¡El 3D Secure fue aprobado por el banco!
                            // Ahora le avisamos al backend para que procese el alta en la DB local
                            await PaymentModel.confirmSubscription({
                                orderId: subscription.orderId, // El id de la suscripción de stripe
                                platform: 'stripe',
                                type: type
                            });
                        }
                    } else {
                        throw new Error("El pago requiere autenticación, pero faltan credenciales de verificación.");
                    }
                }

                // 4. Registrar el pago localmente en tu DB (Solo llegamos aquí si el pago fue exitoso o pasó el 3D Secure)
                await PaymentModel.createPayment({
                    amount,
                    currency,
                    description,
                    planId,
                    type,
                    platform: 'stripe',
                    brand: paymentMethod.card.brand,
                    last4: paymentMethod.card.last4,
                    orderId: subscription.orderId
                });

                onSuccess();

            } catch (error) {
                setFormProcessing(false);
                onError(error);
            } finally {
                dispatch(closeLoader());
            }
        }
    };

    const CARD_ELEMENT_OPTIONS = {
        style: {
            base: { fontSmoothing: 'antialiased', fontSize: '1em' },
            invalid: { color: '#fa755a', iconColor: '#fa755a' }
        },
        hidePostalCode: true
    };

    return (
        <Form onSubmit={handleSubmit}>
            {paymentRequest && (
                <PaymentRequestButtonElement options={{ paymentRequest }} />
            )}
            <Form.Field>
                <label>{I18n.t('stripe.cardNumber')}</label>
                <CardNumberElement id='card_number' options={CARD_ELEMENT_OPTIONS} />
            </Form.Field>
            <Form.Group>
                <div className='cl-12'>
                    <Form.Field>
                        <label>{I18n.t('stripe.expirationDate')}</label>
                        <CardExpiryElement id='card_expiry' options={CARD_ELEMENT_OPTIONS} />
                    </Form.Field>
                </div>
                <div className='cl-4'>
                    <Form.Field>
                        <label>CVC</label>
                        <CardCvcElement id='card_cvc' options={CARD_ELEMENT_OPTIONS} />
                    </Form.Field>
                </div>
            </Form.Group>
            {formProcessing ?
                <OButton type='button' disabled={true} $terciary fluid $color='#fff' $upper>{I18n.t('setup.processingPayment')}</OButton> :
                <OButton type='submit' disabled={!stripe} $terciary fluid $color='#fff' $upper>{I18n.t('setup.proccessPayment')}</OButton>
            }
        </Form>
    );
}

export default StripeCheckoutForm;